import { Button, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField } from "@mui/material";
import { createListStore } from "../../common/listStore";
import { renderClose, useDialog, useSimpleDialog } from "../../common/dialog";
import { useEffect, useRef, useState } from "react";
import { CustomerDto, emptyContinuousOrder, newContinuousOrder, ContinuousOrderEditDto, OrderProductDto, RetentionSaveDto } from "./types";
import { catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, timer } from "rxjs";
import { useDefaultValues } from "../../common/defaultValues";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { propBoolField, propDateField, propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import shallow from "zustand/shallow";
import { propInt, propMediaCode, propNumber, propRequired, propValidValue } from "../../common/entityStoreValidators";
import { customerOrderDialogTitle, getOrderState, useAddressList, useCustomerBasicInfo } from "./common";
import { OrderStateId, RetentionCodeId, useDeliveryMethods, useOrderStates } from "../../common/enum";
import { ifNull, useRxJsSub } from "../../common/utils";
import { axiosObservable, createAxios } from "../../common/axios";
import { RetentionDialog } from "./RetentionDialog";
import { Redo } from "@mui/icons-material";
import { UserTypeId, useAccountStore } from "../../common/account";
import { InputSizedButtonSx } from "../../common/form";
import { DeliveryPlaceDialog } from "./DeliveryPlaceDialog";
import { EntityStoreSaveButton } from "../../common/SaveButton";

export function ContinuousOrderDialog(props: {
    orderId: number | null;
    close: () => void;
    useCustomerStore: UseEntityStore<CustomerDto>;
    onRetentionSelected(retention: RetentionSaveDto): Promise<boolean>;
    retention?: RetentionSaveDto;
    clearRetention?(): void;
}) {
    const userTypeId = useAccountStore(p => p.data?.accessTokenDecoded.userTypeId);
    const isEditRole = userTypeId !== UserTypeId.CollectionAgency && userTypeId !== UserTypeId.View;
    const customer = useCustomerBasicInfo(props.useCustomerStore);
    const def = useDefaultValues();
    const [{ useOrderStore, useProductsStore, useProjectsStore, setSaveProps }] = useState(() => {
        const useOrderStore = createEntityStore<ContinuousOrderEditDto>({
            path: "customers/continuousOrders",
            id: props.orderId,
            data: props.orderId == null ? newContinuousOrder(props.useCustomerStore, props.retention) : emptyContinuousOrder(customer.id!),
            getNewId: p => p.id!,
            onSaved() {
                if (props.clearRetention != null) {
                    props.clearRetention();
                }
                useOrderStore.getState().setClientDataProp("retention", null);
                props.useCustomerStore.getState().fetchServerData();
                props.close();
            },
        });

        const useProductsStore = createListStore<OrderProductDto>({
            path: `customers/continuousOrders/getProductsAndProjects?orderId=${props.orderId == null ? "" : props.orderId}`,
        });

        const useProjectsStore = createListStore({
            data: combineLatest([
                useProductsStore.getState().items$,
                useOrderStore.getState().clientData$.pipe(map(p => ({ productId: p.data.productId, isFetching: p.isFetching })), distinctUntilChanged(shallow))
            ]).pipe(map(p => ({ items: p[0].items.find(s => s.id === p[1].productId)?.projects || [], isFetching: p[0].isFetching || p[1].isFetching })))
        });

        function setSaveProps(retention: boolean) {
            const saveProps: (keyof ContinuousOrderEditDto)[] = ["customerId", "projectId", "orderStateId", "qty", "sc", "discPct", "act", "deliveryAddressId",
                "deliveryMethodId", "deliveryMethodPlaceId"];
            if (retention) {
                saveProps.push("retention");
            }
            useOrderStore.getState().setSaveProps(saveProps);
        }
        setSaveProps(true);

        return { useOrderStore, useProductsStore, useProjectsStore, setSaveProps };
    });
    const orderStore = useOrderStore(p => ({
        serverData: p.serverData, hasShipments: p.clientData.hasShipments, isFetching: p.isFetching,
        isSaveDisabled: p.isSaveDisabled, isFetched: p.isFetched,
        projectId: p.clientData.projectId, productId: p.clientData.productId,
        orderStateId: p.clientData.orderStateId, act: p.clientData.act,
        entityId: p.entityId, deliveryMethodId: p.clientData.deliveryMethodId,
        deliveryMethodPlaceId: p.clientData.deliveryMethodPlaceId,
        deliveryAddressId: p.clientData.deliveryAddressId,
    }));

    const productsStore = useProductsStore();
    const projectsStore = useProjectsStore();

    useEffect(() => {
        const maxQty = productsStore.items.filter(p => p.projects.some(p => p.id === orderStore.projectId)).map(p => p.maxShipmentUnit)[0];
        const maxDiscount = ifNull(projectsStore.items.filter(p => p.id === orderStore.projectId).map(p => p.maxDiscount).at(0), 25);
        useOrderStore.getState().setPropValidators([
            propRequired("projectId"),

            propValidValue("qty", "qtyFormatted"),
            propRequired("qty"),
            propInt("qty"),
            propNumber("qty", p => orderStore.projectId != null && (p < 1 || p > maxQty) && `Quantity can be between 1 and ${maxQty}.`),

            propValidValue("discPct", "discPctFormatted"),
            propRequired("discPct"),
            propInt("discPct"),
            propNumber("discPct", p => p !== 0 && (p < 5 || p > maxDiscount) && `Discount can be between 5 and ${maxDiscount}.`),

            ...propMediaCode("sc"),
            propRequired("deliveryMethodId"),
        ]);
    }, [orderStore, useOrderStore, orderStore.projectId, productsStore.items, projectsStore.items]);

    useEffect(() => {
        var sub = useOrderStore.getState().clientData$.pipe(
            map(p => ({ qty: p.data.qty, projectId: p.data.projectId, orderId: p.data.id, discPct: p.data.discPct })),
            distinctUntilChanged(shallow),
            switchMap(params => params.projectId == null || params.qty == null || params.discPct == null ? of(null) :
                timer(250).pipe(
                    switchMap(() => axiosObservable(signal => createAxios().get(`customers/continuousOrders/getPrice`, { params, signal }))),
                    catchError(() => of(null)))),
        ).subscribe({
            next(data) {
                useOrderStore.getState().setClientDataProp("toPay", data?.toPay);
                useOrderStore.getState().setClientDataProp("totalPrice", data?.totalPrice);
            }
        });
        return () => sub.unsubscribe();
    }, [useOrderStore]);

    useEffect(() => {
        var sub = combineLatest([useOrderStore.getState().clientData$, useOrderStore.getState().serverData$]).subscribe({
            next([{ data: clientData }, { data: serverData }]) {
                if (clientData.retention?.retentionCodeId === RetentionCodeId.NewWP) {
                    setSaveProps(serverData.orderStateId !== clientData.orderStateId && clientData.orderStateId === OrderStateId.NoShipment);
                }
            }
        });
        return () => sub.unsubscribe();
    }, [useOrderStore, setSaveProps]);

    useEffect(() => {
        if (props.orderId == null && orderStore.productId == null && productsStore.items.length > 0) {
            useOrderStore.getState().setClientDataProp("productId", productsStore.items[0].id);
        }
    }, [props.orderId, orderStore, useOrderStore, orderStore.productId, productsStore.items]);

    const deliveryMethods = useDeliveryMethods(p => p.items);
    const deliveryMethod = deliveryMethods.find(p => p.id === orderStore.deliveryMethodId);

    // same logic is in TM and MO
    useRxJsSub(() => combineLatest([
        useProjectsStore.getState().items$,
        useOrderStore.getState().clientData$.pipe(map(p => p.data.projectId), distinctUntilChanged(), filter(p => p != null))
    ]).subscribe(([projects, projectId]) => {
        const deliveryMethodId = projects.items.find(p => p.id === projectId)?.deliveryMethodId ?? null;
        if (deliveryMethodId != null && deliveryMethodId !== useOrderStore.getState().clientData.deliveryMethodId) {
            useOrderStore.getState().setServerDataProp("deliveryMethodId", deliveryMethodId);
            useOrderStore.getState().setClientDataProp("deliveryMethodId", deliveryMethodId);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceId", null);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceName", null);
        }
    }));

    const hasShipments = orderStore.hasShipments;
    const orderStates = useOrderStates(p => p.items);
    const orderState = getOrderState(orderStore, orderStates);

    function makeOrderReshipStarter() {
        const store = useOrderStore.getState();
        store.setClientDataProp("orderStateId", OrderStateId.NoShipment);
        store.setClientDataProp("act", true);
    }

    function makeOrderActive() {
        const store = useOrderStore.getState();
        if (store.clientData.orderStateId !== OrderStateId.DelStopped) {
            store.setClientDataProp("orderStateId", OrderStateId.Active);
        }
        store.setClientDataProp("act", true);
    }

    function makeOrderPassive() {
        const store = useOrderStore.getState();
        if (store.clientData.orderStateId !== OrderStateId.DelStopped) {
            store.setClientDataProp("orderStateId", OrderStateId.Passive);
        }
        store.setClientDataProp("act", false);
    }

    function onProductChange() {
        useOrderStore.getState().setClientDataProp("projectId", null);
    }

    function onProjectChange(projectId: number) {
        const project = useProjectsStore.getState().items.find(p => p.id === projectId);
        useOrderStore.getState().setClientDataProp("qty", project!.defaultShipmentUnit);
        useOrderStore.getState().setClientDataProp("qtyFormatted", undefined);
    }

    const simpleDialog = useSimpleDialog();

    async function sendGift(retention: RetentionSaveDto) {
        const { data } = await createAxios().post<{ error: string; }>(`customers/continuousOrders/${useOrderStore.getState().entityId}/sendGift`, { retention });
        if (data.error != null) {
            simpleDialog.show("Error", data.error);
        }
    }

    const quantityRef = useRef<HTMLInputElement>();
    const discountRef = useRef<HTMLInputElement>();

    const retentionDialog = useDialog(close => <RetentionDialog close={close} orderId={props.orderId!} onRetentionSelected={async retention => {
        if ([RetentionCodeId.DecreaseSu, RetentionCodeId.IncreaseSu, RetentionCodeId.Discount].includes(retention.retentionCodeId)) {
            useOrderStore.getState().setClientDataProp("retention", retention);
            if ([RetentionCodeId.DecreaseSu, RetentionCodeId.IncreaseSu].includes(retention.retentionCodeId)) {
                setTimeout(() => {
                    quantityRef.current?.focus();
                });
            }
            if ([RetentionCodeId.Discount].includes(retention.retentionCodeId)) {
                setTimeout(() => {
                    discountRef.current?.focus();
                });
            }
            return true;
        }
        if (retention.retentionCodeId === RetentionCodeId.FreeGift) {
            await sendGift(retention);
            return true;
        }
        if (retention.retentionCodeId === RetentionCodeId.NewWP) {
            makeOrderReshipStarter();
            useOrderStore.getState().setClientDataProp("retention", retention);
            return true;
        }
        const result = await props.onRetentionSelected(retention);
        if (result) {
            props.close();
        }
        return result;
    }} />);

    const deliveryPlaceDialog = useDialog((close, dialogProps) => <DeliveryPlaceDialog close={close} dialogProps={dialogProps} orderId={props.orderId}
        deliveryMethodId={orderStore.deliveryMethodId!} deliveryMethodPlaceId={orderStore.deliveryMethodPlaceId ?? null}
        changeDeliveryMethodPlace={(id, name) => {
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceId", id);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceName", name);
        }}
        getCustomerAddress={() => {
            const customer = props.useCustomerStore.getState().serverData;
            const order = useOrderStore.getState().clientData;
            return order.deliveryAddressId == null ? customer :
                customer.customerAddresses.find(p => p.id === order.deliveryAddressId)!;
        }} />, undefined, { usingCustomDialogTag: true});
    async function showDeliveryMethodPlaceDialog() {
        if (useOrderStore.getState().clientData.deliveryMethodId != null) {
            deliveryPlaceDialog.show();
        }
    }

    const { addressListDialog, useCustomerAddressListStore } = useAddressList(props.useCustomerStore, !isEditRole);

    return <>
        {simpleDialog.render()}
        {retentionDialog.render()}
        {addressListDialog.render()}
        {deliveryPlaceDialog.render()}
        <DialogTitle>{customerOrderDialogTitle(orderStore.serverData.ordId, orderStore.isFetching, isNew => isNew ? "New order" : "Edit order", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={12}>{propEnumField(useOrderStore)("productId", "Product", useProductsStore, { disabled: !isEditRole || hasShipments, onChange: onProductChange })}</Grid>
                <Grid item xs={12}>{propEnumField(useOrderStore)("projectId", "Project", useProjectsStore, { disabled: !isEditRole || hasShipments, onChange: onProjectChange })}</Grid>
                <Grid item xs={6}>{propTextField(useOrderStore)("sc", "Media code", { disabled: !isEditRole || hasShipments })}</Grid>
                <Grid item xs={6}><TextField value={def.clientData.mailId} label="Mail ID" disabled /></Grid>
                {deliveryMethod?.hasPlaces && <Grid item container xs={12} spacing={2}>
                    <Grid item xs>{propTextField(useOrderStore)("deliveryMethodPlaceName", `${deliveryMethod?.name ?? ""} parcel shop`, { readOnly: true, disabled: !isEditRole })}</Grid>
                    <Grid item><Button disabled={!isEditRole || deliveryMethod == null ? true : !deliveryMethod.hasPlaces} sx={InputSizedButtonSx} onClick={showDeliveryMethodPlaceDialog}>Change</Button></Grid>
                </Grid>}
                <Grid item container xs={12} spacing={2}>
                    <Grid item xs>{propEnumField(useOrderStore)("deliveryAddressId", "Customer's delivery address", useCustomerAddressListStore,
                        { disabled: !isEditRole, autoComplete: false, addNone: true })}</Grid>
                    <Grid item><Button disabled={!isEditRole} sx={InputSizedButtonSx} onClick={() => addressListDialog.show()}>Manage</Button></Grid>
                </Grid>
                <Grid item xs={12}>{propBoolField(useOrderStore)("confirmedByCustomer", "Confirmed by customer", { disabled: true })}</Grid>
                <Grid item xs={12}><Stack direction="row" spacing={2} alignItems="center">
                    <TextField value={orderState} label="Order state" disabled />
                    <Button sx={InputSizedButtonSx} title="Reship starter" onClick={makeOrderReshipStarter}
                        disabled={!isEditRole || orderStore.isFetching ||
                            orderStore.orderStateId === OrderStateId.NoShipment ||
                            orderStore.orderStateId === OrderStateId.DelStopped
                        }><Redo /></Button>
                    <Button sx={InputSizedButtonSx} title="Make order active" onClick={makeOrderActive}
                        disabled={!isEditRole || orderStore.isFetching ||
                            orderStore.orderStateId === OrderStateId.Active ||
                            (orderStore.orderStateId === OrderStateId.DelStopped && orderStore.act === true)
                        } color="positive">A</Button>
                    <Button sx={InputSizedButtonSx} title="Make order passive" onClick={makeOrderPassive}
                        disabled={!isEditRole || orderStore.isFetching ||
                            orderStore.orderStateId === OrderStateId.Passive ||
                            (orderStore.orderStateId === OrderStateId.DelStopped && orderStore.act === false)
                        } color="negative">P</Button>
                </Stack></Grid>
                <Grid item xs={6}>{propDateField(useOrderStore)("ordDte", "ordDteFormatted", "Enrolment date", { disabled: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useOrderStore)("qty", "qtyFormatted", "Ordered quantity", { inputRef: quantityRef, disabled: !isEditRole })}</Grid>
                <Grid item xs={6}>{propNumberField(useOrderStore)("totalPrice", null, "Total price", { isCurrency: true, disabled: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useOrderStore)("discPct", "discPctFormatted", "Discount", { unit: "%", inputRef: discountRef, disabled: !isEditRole })}</Grid>
                <Grid item xs={6}>{propNumberField(useOrderStore)("toPay", null, "To pay", { isCurrency: true, disabled: true })}</Grid>
                <Grid item xs={6}></Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button disabled={!isEditRole || orderStore.isFetching || props.orderId == null || !orderStore.isSaveDisabled} onClick={() => retentionDialog.show()}>Retention</Button>
            <EntityStoreSaveButton useStore={useOrderStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}
